import { useUpdateSalesBOMJob } from '@hooks/useUpdateSalesBOMJob';
import { useInvalidateQueries } from '@partful/shared/lib/hooks/useInvalidateQueries';
import { type SalesBomJob } from '@providers/job/types';
import { postSBOM, postSBOMIngest } from '@services/productInventory';
import { useMutation } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { type SalesBOMFormSchema } from './lib/SalesBOMFormSchema';

export const useCancelSBOMJob = (accountId: string, runID?: string) => {
  const { mutateAsync: updateSalesBOMJob } = useUpdateSalesBOMJob();

  const cancelJob = async () => {
    await updateSalesBOMJob({ runID: runID!, accountId, status: 'CANCELLED_BY_USER' });
  };

  return { cancelJob };
};

export function useSalesBOMForm() {
  const {
    reset: resetForm,
    formState: { isSubmitSuccessful, isLoading: isLoadingForm, isSubmitting: isSubmittingForm },
    watch,
  } = useFormContext<SalesBOMFormSchema>();
  const { file } = watch();

  return { resetForm, isSubmitSuccessful, isLoadingForm, isSubmittingForm, file };
}

export function useSalesBOMPageLinks(
  configurationPID: string,
  productName: string,
  salesBOMJobData: SalesBomJob | undefined,
  runID: string
) {
  const nextUrlSearchParams = new URLSearchParams({
    configurationPID,
    productName,
    salesBOMJobRunID: salesBOMJobData?.runID ?? '',
    cadJobRunID: runID,
  }).toString();

  const previousUrlSearchParams = new URLSearchParams({
    configurationPID,
    productName,
    runID,
  }).toString();

  const nextLink = `/create-product/match-report?${nextUrlSearchParams}`;
  const previousLink = `/create-product/cad/select-part-number?${previousUrlSearchParams}`;

  return { nextLink, previousLink };
}

export function useCreateSalesBOMJob(accountId: string, configurationPID: string) {
  const { invalidateContentProcessingJobsQueries } = useInvalidateQueries();

  return useMutation(['createSalesBOMJob'], () => postSBOM(configurationPID, accountId), {
    onSuccess: async () => {
      await invalidateContentProcessingJobsQueries();
    },
  });
}

export function useSalesBOMPostIngest(accountId: string, cadJobRunID: string) {
  return useMutation(['postSBOMIngest'], (salesJobRunID: string) =>
    postSBOMIngest(accountId, salesJobRunID, { cadJobRunID })
  );
}
